import React, { useEffect, useState } from 'react';

import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useIsManagerView from 'screens/performanceReview/hooks/useIsManagerView';
import { checkAIReviewManagerSummaryEnabled } from 'utils/helpers/featureFlags';

import { Summarize } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { ReviewGoalSummary } from 'uiToolkitV1/ReviewGoalSummary';
import { ScrollableSectionNav } from 'uiToolkitV1/ScrollableSectionNav';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import ManagerAISummary from '../../ManagerAISummary';
import ManagerAISummaryMultiQuestions from '../../ManagerAISummary/ManagerAISummaryMultiQuestions';
import { checkFeedbackHasReviewsForAISummary } from '../../OverallQuestionsForm/components/QNAScrollableNav';
import PeerFeedbackRenderer from '../../PeerFeedback/PeerFeedbackRenderer';
import usePeerFeedbackData from '../../PeerFeedback/hooks/dataHooks';

const SummarizeIcon = ({ ...props }) => <Summarize boxSize="20px" {...props} />;

const options = [
  {
    label: 'Summary',
    Icon: SummarizeIcon,
  },
];

const GoalScrollableNavSingle = ({
  question,
  answerQuestionHandler,
  reviewId,
  revieweeId,
  isManagerAISummaryEnabled,
  isManagerView,
  okr,
  goalId,
  providerType,
  hasMultipleQuestions = false,
  questionIndex,
  navOptions,
}) => {
  const { description: questionText, id: questionId } = question;
  const { isPreview } = useReviewPreviewParams();
  const { data = [], isLoading } = usePeerFeedbackData({
    goalId: okr?.uuid ?? goalId,
    questionId,
    isManagerView,
  });

  const isManagerAISummaryApplicable =
    question?.enableFeedbackText &&
    isManagerAISummaryEnabled &&
    isManagerView &&
    [REVIEW_PROVIDER_TYPES.MANAGER, REVIEW_PROVIDER_TYPES.DLM].includes(providerType) &&
    checkAIReviewManagerSummaryEnabled() &&
    data &&
    checkFeedbackHasReviewsForAISummary(data);

  if (isManagerView && isLoading) {
    return (
      <Stack>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }
  if (!hasMultipleQuestions && !isMobileView()) {
    return (
      <ScrollableSectionNav options={navOptions}>
        <Box bg="#F5F7F9" p="16px">
          {isManagerAISummaryApplicable && !isPreview && (
            <ManagerAISummary
              pasteHandler={answerQuestionHandler}
              feedbackTextHtml={question.feedbackTextHtml}
              feedbackText={question.feedbackText}
              goalText={okr?.description ?? questionText}
              reviewId={reviewId}
              revieweeId={revieweeId}
              questionId={questionId}
              goalId={okr?.uuid ?? goalId}
              isViewOnly={question?.isViewOnlyModeEnabled}
              goalProgressLevel={okr?.avgProgressPercentage}
            />
          )}
          <ReviewGoalSummary okr={okr} />
        </Box>
        <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column">
          {' '}
          <TextBodyMedium bold>Feedback</TextBodyMedium>
          <TextBodyMedium mt="16px">{questionText}</TextBodyMedium>
        </Box>
        {data?.length === 0 && (
          <TextBodyMedium p={4} bold color="text.g3">
            No Responses
          </TextBodyMedium>
        )}
        {data?.map((feedback, index) => {
          const {
            employee,
            feedbackTextHtml,
            feedbackText,
            // eslint-disable-next-line no-shadow
            providerType,
            selectedAnswerOptions,
            peerType,
          } = feedback ?? {};
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column" key={index}>
              <Flex flexDir="column">
                <PeerFeedbackRenderer
                  employee={employee}
                  feedbackTextHtml={feedbackTextHtml}
                  feedbackText={feedbackText}
                  providerType={providerType}
                  selectedAnswerOptions={selectedAnswerOptions}
                  question={question}
                  peerType={peerType}
                />
              </Flex>
            </Box>
          );
        })}
      </ScrollableSectionNav>
    );
  }

  return (
    <Box>
      <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column">
        <TextBodyMedium mt="16px">
          {hasMultipleQuestions && `${questionIndex + 1}. `}
          {questionText}
        </TextBodyMedium>
      </Box>
      {data?.length === 0 && (
        <TextBodyMedium p={4} bold color="text.g3">
          No Responses
        </TextBodyMedium>
      )}
      {data?.map((feedback, index) => {
        const {
          employee,
          feedbackTextHtml,
          feedbackText,
          // eslint-disable-next-line no-shadow
          providerType,
          selectedAnswerOptions,
          peerType,
        } = feedback ?? {};
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column" key={index}>
            <Flex flexDir="column">
              <PeerFeedbackRenderer
                employee={employee}
                feedbackTextHtml={feedbackTextHtml}
                feedbackText={feedbackText}
                providerType={providerType}
                selectedAnswerOptions={selectedAnswerOptions}
                question={question}
                peerType={peerType}
              />
            </Flex>
          </Box>
        );
      })}
    </Box>
  );
};

const GoalScrollableNav = ({
  goalId,
  okr,
  providerType,
  answerQuestionHandler,
  isManagerAISummaryEnabled = false,
  reviewId,
  revieweeId,
  questions,
}) => {
  const { isManagerView } = useIsManagerView();

  const [managerOptions, setManagerOptions] = useState([]);

  useEffect(() => {
    if (isManagerView && questions.length > 1) {
      const optionsArray = questions?.map(({ employee }, index) => ({
        label: questions.length <= 1 ? null : `Q${index + 1}`,
        src: questions.length > 1 ? null : employee?.profilePhotoSrc,
      }));
      setManagerOptions([...options, ...optionsArray]);
    }
  }, [isManagerView, questions]);

  const navOptions = isManagerView ? managerOptions : options;

  const isManagerAISummaryApplicable =
    questions.some(({ enableFeedbackText }) => enableFeedbackText) &&
    isManagerAISummaryEnabled &&
    isManagerView &&
    [REVIEW_PROVIDER_TYPES.MANAGER, REVIEW_PROVIDER_TYPES.DLM].includes(providerType) &&
    checkAIReviewManagerSummaryEnabled();
  const { isPreview } = useReviewPreviewParams();

  if (!isManagerView && !goalId) {
    return null;
  }

  if (!isManagerView) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '462px',

          '.scrollableSection': {
            background: '#F5F7F9',
          },
          flexGrow: 1,
          maxHeight: '100%',
        }}
      >
        <ScrollableSectionNav options={navOptions}>
          <Box bg="#F5F7F9" p="16px">
            <ReviewGoalSummary okr={okr} />
          </Box>
        </ScrollableSectionNav>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '462px',
        '.scrollableSection': {
          background: '#F5F7F9',
        },
        maxHeight: '100%',
      }}
    >
      <>
        {questions.length === 1 && !isMobileView() && (
          <GoalScrollableNavSingle
            {...{
              question: questions[0],
              answerQuestionHandler,
              reviewId,
              revieweeId,
              isManagerAISummaryEnabled,
              isManagerView,
              providerType,
              okr,
              goalId,
              navOptions,
            }}
            questionIndex={0}
            hasMultipleQuestions={false}
          />
        )}

        {questions.length > 1 && !isMobileView() && (
          <ScrollableSectionNav options={navOptions}>
            <Box>
              {isManagerAISummaryApplicable && !isPreview && (
                <Box m={4} borderBottom="1px solid #EBEFF3">
                  <ManagerAISummaryMultiQuestions
                    questions={questions}
                    pasteHandler={answerQuestionHandler}
                    goalText={okr?.description ?? ''}
                    reviewId={reviewId}
                    revieweeId={revieweeId}
                    goalId={okr?.uuid ?? goalId}
                    goalProgressLevel={okr?.avgProgressPercentage}
                  />
                </Box>
              )}
              <ReviewGoalSummary okr={okr} />
            </Box>

            <Box bg="#F5F7F9" p="16px" display="flex" flexDir="column">
              <TextBodyMedium bold>Feedback</TextBodyMedium>
            </Box>
            {questions?.map((curQues, index) => (
              <GoalScrollableNavSingle
                {...{
                  question: curQues,
                  answerQuestionHandler,
                  reviewId,
                  revieweeId,
                  isManagerAISummaryEnabled,
                  isManagerView,
                  providerType,
                  okr,
                  goalId,
                  navOptions,
                }}
                questionIndex={index}
                hasMultipleQuestions
              />
            ))}
          </ScrollableSectionNav>
        )}
      </>
    </Box>
  );
};

export default GoalScrollableNav;
